import type { ReactNode } from "react";

import type { AuthMode } from "@/components/auth/authMode";
import { Button } from "@/components/ui/button";
import { DEFAULT_TRIAL_DURATION_DAYS } from "@shared/constants/trial";
import asrLogoChalk from "@assets/logo/ASR-Primary-Chalk.webp";

import { CheckCircle2 } from "lucide-react";
import { Link } from "wouter";

export interface AuthBrandPanelProps {
  mode: AuthMode;
  hasPendingInvite?: boolean;
}

const DEFAULT_VALUE_PROPS = [
  "Digital checklists and audits for every site",
  "Track actions, follow-ups and compliance in one place",
  "Invite your team and assign roles in minutes",
];

const FREE_TRIAL_VALUE_PROPS = [
  `${DEFAULT_TRIAL_DURATION_DAYS}-day free trial with full platform access`,
  "Digital checklists and audits for every site",
  "Track actions, follow-ups and compliance in one place",
];

function getBrandCopy(
  mode: AuthMode,
  hasPendingInvite = false,
): {
  headline: ReactNode;
  valueProps: string[];
  ctaPrompt: string;
  ctaLabel: string;
  ctaHref: string;
} {
  if (mode === "sign-in") {
    return {
      headline: (
        <>
          Safety and compliance,
          <br />
          made simple.
        </>
      ),
      valueProps: DEFAULT_VALUE_PROPS,
      ctaPrompt: "New to A Safer Risk?",
      ctaLabel: "Start your free trial",
      ctaHref: "/free-trial",
    };
  }

  if (mode === "free-trial") {
    return {
      headline: (
        <>
          Your {DEFAULT_TRIAL_DURATION_DAYS}-day journey
          <br />
          starts here.
        </>
      ),
      valueProps: FREE_TRIAL_VALUE_PROPS,
      ctaPrompt: "Already have an account?",
      ctaLabel: "Sign in",
      ctaHref: "/login",
    };
  }

  if (mode === "recovery") {
    if (hasPendingInvite) {
      return {
        headline: <>You&apos;re invited.</>,
        valueProps: [
          "Accept your invitation to join your organisation",
          "Access checklists, audits and compliance tools for your sites",
          "Start collaborating with your team straight away",
        ],
        ctaPrompt: "Signed in with the wrong account?",
        ctaLabel: "Sign out",
        ctaHref: "/logout",
      };
    }

    return {
      headline: (
        <>
          You&apos;re almost
          <br />
          connected.
        </>
      ),
      valueProps: [
        "Create a workspace for your organisation",
        "Accept an invitation from your administrator",
        "Get help if you need access to an existing team",
      ],
      ctaPrompt: "Signed in with the wrong account?",
      ctaLabel: "Sign out",
      ctaHref: "/logout",
    };
  }

  return {
    headline: (
      <>
        Safety and compliance,
        <br />
        made simple.
      </>
    ),
    valueProps: DEFAULT_VALUE_PROPS,
    ctaPrompt: "Already have an account?",
    ctaLabel: "Sign in",
    ctaHref: "/login",
  };
}

export function AuthBrandPanel({ mode, hasPendingInvite = false }: AuthBrandPanelProps) {
  const { headline, valueProps, ctaPrompt, ctaLabel, ctaHref } = getBrandCopy(
    mode,
    hasPendingInvite,
  );

  return (
    <div className="relative hidden h-full flex-col justify-between gap-10 bg-brand-midnight p-10 md:flex">
      <img
        src={asrLogoChalk}
        alt="A Safer Risk"
        className="w-44 self-start"
        width={176}
        height={55}
      />

      <div className="space-y-6">
        <h1 className="text-3xl font-semibold leading-tight tracking-tight text-white">{headline}</h1>
        <ul className="space-y-3">
          {valueProps.map((prop) => (
            <li key={prop} className="flex items-start gap-3 text-sm text-white/80">
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-brand-teal" aria-hidden />
              <span>{prop}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="space-y-3">
        <p className="text-sm text-white/70">{ctaPrompt}</p>
        <Button
          asChild
          variant="outline"
          className="border-white/30 bg-transparent text-white hover:bg-white/10 hover:text-white"
        >
          <Link href={ctaHref}>{ctaLabel}</Link>
        </Button>
      </div>
    </div>
  );
}
